import { useEffect, useMemo, useState } from 'react';

/** When list length is greater than this, paginate; otherwise show the full list. */
export const CLIENT_PAGE_SIZE = 10;

export function useClientPagination<T>(items: readonly T[], ...resetDeps: unknown[]) {
  const usePaging = items.length > CLIENT_PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(items.length / CLIENT_PAGE_SIZE));
  const [page, setPage] = useState(1);

  const resetKey = JSON.stringify(resetDeps);

  useEffect(() => {
    setPage(1);
  }, [resetKey]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pagedItems = useMemo(() => {
    if (!usePaging) {
      return items.slice() as T[];
    }
    const start = (page - 1) * CLIENT_PAGE_SIZE;
    return items.slice(start, start + CLIENT_PAGE_SIZE) as T[];
  }, [items, page, usePaging]);

  const rangeStart = usePaging ? (page - 1) * CLIENT_PAGE_SIZE + 1 : items.length === 0 ? 0 : 1;
  const rangeEnd = usePaging ? Math.min(page * CLIENT_PAGE_SIZE, items.length) : items.length;

  return {
    pagedItems,
    page,
    setPage,
    totalPages,
    usePaging,
    pageSize: CLIENT_PAGE_SIZE,
    totalItems: items.length,
    rangeStart,
    rangeEnd,
  };
}
